/**
 * Typography Tokens
 * Font families, sizes, weights, and line heights
 */

/**
 * Font families
 */
export const fontFamilies = {
  heading: 'Epilogue, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  body: 'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
} as const;

/**
 * Font sizes (rem-based for accessibility)
 */
export const fontSizes = {
  xs: '0.75rem', // 12px
  sm: '0.875rem', // 14px
  base: '1rem', // 16px
  lg: '1.125rem', // 18px
  xl: '1.25rem', // 20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem', // 48px
  '6xl': '3.75rem', // 60px
  '7xl': '4.5rem', // 72px
  '8xl': '6rem', // 96px
  '9xl': '8rem', // 128px
} as const;

/**
 * Font weights
 */
export const fontWeights = {
  thin: '100',
  extralight: '200',
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
} as const;

/**
 * Line heights
 */
export const lineHeights = {
  none: '1',
  tight: '1.25',
  snug: '1.375',
  normal: '1.5',
  relaxed: '1.625',
  loose: '2',
} as const;

/**
 * Letter spacing
 */
export const letterSpacing = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0em',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const;

/**
 * Font size with line height tuples (for Tailwind)
 */
export const fontSizeWithLineHeight = {
  xs: [fontSizes.xs, { lineHeight: lineHeights.tight }],
  sm: [fontSizes.sm, { lineHeight: lineHeights.tight }],
  base: [fontSizes.base, { lineHeight: lineHeights.normal }],
  lg: [fontSizes.lg, { lineHeight: lineHeights.relaxed }],
  xl: [fontSizes.xl, { lineHeight: lineHeights.relaxed }],
  '2xl': [fontSizes['2xl'], { lineHeight: lineHeights.snug }],
  '3xl': [fontSizes['3xl'], { lineHeight: lineHeights.snug }],
  '4xl': [fontSizes['4xl'], { lineHeight: lineHeights.tight }],
  '5xl': [fontSizes['5xl'], { lineHeight: lineHeights.none }],
  '6xl': [fontSizes['6xl'], { lineHeight: lineHeights.none }],
  '7xl': [fontSizes['7xl'], { lineHeight: lineHeights.none }],
  '8xl': [fontSizes['8xl'], { lineHeight: lineHeights.none }],
  '9xl': [fontSizes['9xl'], { lineHeight: lineHeights.none }],
} as const;

export type FontFamily = keyof typeof fontFamilies;
export type FontSize = keyof typeof fontSizes;
export type FontWeight = keyof typeof fontWeights;
export type LineHeight = keyof typeof lineHeights;
export type LetterSpacing = keyof typeof letterSpacing;
